import { ShippingManifestSummary, VesselPositions } from '@/types';

export type VesselTrackStatus = 'enRoute' | 'atPort';

export const vesselTrackStatus = (status: number): VesselTrackStatus =>
  status === 1 || status === 5 || status === 6 ? 'atPort' : 'enRoute';

const countryCode = (value?: string | null) => {
  const text = value?.trim() ?? '';
  if (/^[A-Za-z]{2}$/.test(text)) return text.toUpperCase();
  if (/^[A-Za-z]{5}$/.test(text)) return text.slice(0, 2).toUpperCase();
  return '';
};

export const portWithCountry = (
  port?: string | null,
  country?: string | null,
) => {
  const name = port?.trim() ?? '';
  if (!name) return '';
  const code = countryCode(country);
  return code ? `${name} (${code})` : name;
};

export const formatPosition = (lat: number, lon: number) => {
  const latHemisphere = lat >= 0 ? 'N' : 'S';
  const lonHemisphere = lon >= 0 ? 'E' : 'W';
  return `${Math.abs(lat).toFixed(1)}°${latHemisphere}, ${Math.abs(lon).toFixed(1)}°${lonHemisphere}`;
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const navigableBearing = (value?: number | null) => {
  const bearing = Number(value);
  return bearing >= 0 && bearing < 360 ? bearing : undefined;
};

export const vesselMarkerHeading = (
  heading?: number | null,
  course?: number | null,
) => navigableBearing(heading) ?? navigableBearing(course);

export const vesselMarkerContent = (vessel: VesselPositions) => {
  const speed = Number(vessel.speed || 0).toFixed(1);
  const heading = Math.round(
    vesselMarkerHeading(vessel.heading, vessel.course) ?? 0,
  );
  const name = escapeHtml(vessel.shipName || '');
  return `<div style="min-width:148px;color:#1d2939;line-height:1.45"><div style="font-weight:700">${name}</div><div>${formatPosition(vessel.lat, vessel.lon)}</div><div>${speed} kn / ${heading}°</div></div>`;
};

export const toVesselMapMarkers = (vessels: VesselPositions[]) =>
  vessels.map((vessel) => ({
    id: vessel.mmsi,
    lat: vessel.lat,
    lng: vessel.lon,
    heading: vesselMarkerHeading(vessel.heading, vessel.course),
    content: vesselMarkerContent(vessel),
  }));

export const manifestsForVessel = (
  manifests: ShippingManifestSummary[],
  shipName?: string | null,
) => {
  const name = shipName?.trim().toLowerCase();
  if (!name) return [];
  return manifests.filter(
    (manifest) => manifest.vesselName?.trim().toLowerCase() === name,
  );
};

export const pickNextManifest = (manifests: ShippingManifestSummary[]) => {
  const dated = manifests
    .filter((manifest) => manifest.eta)
    .slice()
    .sort(
      (left, right) =>
        new Date(left.eta as string).getTime() -
        new Date(right.eta as string).getTime(),
    );
  const now = Date.now();
  return (
    dated.find(
      (manifest) => new Date(manifest.eta as string).getTime() >= now,
    ) ||
    dated[0] ||
    manifests[0]
  );
};

export const vesselSites = (vessel: {
  site?: string | null;
  sites?: string[] | null;
}) => {
  const listed = (vessel.sites ?? [])
    .map((site) => site?.trim())
    .filter((site): site is string => Boolean(site));
  if (listed.length > 0) return [...new Set(listed)];
  const site = vessel.site?.trim();
  return site ? [site] : [];
};

export const vesselMatchesSite = (
  vessel: { site?: string | null; sites?: string[] | null },
  site?: string,
) =>
  !site ||
  vesselSites(vessel).some(
    (value) => value.toLowerCase() === site.toLowerCase(),
  );

export const VESSEL_MAP_TABLET_MAX_WIDTH = 1024;
export const VESSEL_MAP_WIDE_MIN_WIDTH = 1701;

export const vesselMapIsStacked = (width: number) =>
  width <= VESSEL_MAP_TABLET_MAX_WIDTH;

export const vesselMapStartsCollapsed = (width: number) =>
  width > VESSEL_MAP_TABLET_MAX_WIDTH && width < VESSEL_MAP_WIDE_MIN_WIDTH;

export const uniqueValues = (values: Array<string | null | undefined>) =>
  [
    ...new Set(
      values
        .map((value) => value?.trim())
        .filter((value): value is string => Boolean(value)),
    ),
  ].sort((left, right) => left.localeCompare(right));
